import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  Typography,
  Divider,
  Paper,
  Skeleton,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { IMAGES } from '../../utils/constants';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import { useUserSignUpComplete, useUserSignUpValidate } from '../../hooks/api/auth';
import { getURLParams } from '../../utils/data-model';
import Footer from '../../components/layout/footer';
import TopNav from '../../components/layout/topNav';
import GuestHeader from '../../components/layout/guest-header';
import { CheckBox } from '@mui/icons-material';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  firstName: ValidateField;
  lastName: ValidateField;
  phone: ValidateField;
};

const SignUpValidate: FunctionComponent = () => {
  const navigate = useNavigate();
  const qparams: any = getURLParams()
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [disabled, setDisabled] = useState<boolean>(true);

  const formValidation: ValidateFieldsState = {
    firstName: { invalid: false, error: '' },
    lastName: { invalid: false, error: '' },
    phone: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const { data: userInfo, isFetched: hasFetched, isLoading, error: queryError } = useUserSignUpValidate(qparams?.token);
  const { mutateAsync: signUpComplete } = useUserSignUpComplete();


  useEffect(() => {
    if (!qparams?.token) {
      navigate('/login');
    }
  }, [navigate, qparams]);

  useEffect(() => {
    if (userInfo) {
      setDisabled(false);
      setEmail(userInfo?.email || '');
      setPassword(userInfo?.tempPassword || '');
    }
  }, [userInfo]);

  // Direct fetch as backup
  useEffect(() => {
    const fetchData = async () => {
      if (!qparams?.token) return;
      try {
        const response = await fetch(`http://localhost:8080/api/v1/signup/validate/${qparams.token}`);
        const data = await response.json();
        console.log('Direct fetch result:', data);
        setEmail(data.email || '');
        setPassword(data.tempPassword || '');
        setDisabled(false);
      } catch (error) {
        console.error('Direct fetch error:', error);
      }
    };
    fetchData();
  }, [qparams?.token]);

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!firstName?.trim()) {
      errors.firstName = { invalid: true, error: "First name is required" };
      valid = false;
    } else {
      errors.firstName = { invalid: false, error: "" };
    }

    if (!lastName?.trim()) {
      errors.lastName = { invalid: true, error: "Last name is required" };
      valid = false;
    } else {
      errors.lastName = { invalid: false, error: "" };
    }

    if (phone?.trim() && !/^\+?[\d\s-]{7,15}$/.test(phone)) {
      errors.phone = {
        invalid: true,
        error: "Please enter a valid phone number",
      };
      valid = false;
    } else {
      errors.phone = { invalid: false, error: "" };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setDisabled(true);
    try {
      if (!qparams?.token) {
        setError('Invalid token. Please try again.');
        setDisabled(false);
        return;
      }
      const result = await signUpComplete({
        token: qparams?.token,
        email: email,
        tempPassword: password,
        firstName: firstName,
        lastName: lastName,
        phone: phone,
      });
      if (result?.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          setSuccess(false);
          navigate('/login');
        }, 3000);
      } else {
        setDisabled(false);
        setError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
          navigate('/forgot-password');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  return (
    <Box 
    // sx={{ 
    //   display: "flex", 
    //   flexDirection: "column", 
    //   minHeight: "100vh" 
    // }}
    >
      <TopNav showFlag={true} />
      <GuestHeader />
      <Box
        sx={{
          backgroundColor: "#fcebed",
          padding: 4,
          mx: "auto",
          mt: 2,
          overflow: 'hidden',
         // flex: 1,
        }}
      >
        <Box sx={{
          width: '92%',
          margin: '0 auto',
          position: 'relative',
        }}>
          <img
            src={IMAGES.extranetLogo}
            alt="Logo"
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              fontSize: 280,
              color: 'rgba(0, 0, 0, 0.05)',
              zIndex: 0,
              opacity: 0.05,
              width: 'auto',
              height: '70px',
            }}
          />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Sign Up
          </Typography>

          <Divider sx={{ borderBottom: "2px solid #c8102e", mb: 2 }} />

          <Typography variant="body2" mb={1}>
            <strong>Extranet</strong>/<span style={{ color: "#c8102e" }}>Sign Up Profile</span>
          </Typography>

          <Box sx={{width: '45%'}}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 0 }}>
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <Grid>
                  <CheckBox sx={{ color: '#c2185b' }} />
                </Grid>
                <Grid size={11}>
                  <Typography variant="body1">
                    {
                      !hasFetched ? (
                        <Skeleton width="100%" sx={{ background: '#eeeeee' }} />
                      ) : (
                        <><strong>Email Address:</strong> {email}</>
                      )
                    }
                  </Typography>
                </Grid>

                <Grid size={12} />
                
                <Grid>
                  <CheckBox sx={{ color: '#c2185b' }} />
                </Grid>
                <Grid size={11}>
                  <Typography variant="body1">
                    {
                      !hasFetched ? (
                        <Skeleton width="100%" sx={{ background: '#eeeeee' }} />
                      ) : (
                        <><strong>Your Password:</strong> {password}</>
                      )
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="body2" sx={{ mb: 1 }}>
              Please save your password before you click the Update button.
            </Typography>

            <Typography variant="caption" color="error">
              *These field are required.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={3}>
              <TextField
                fullWidth
                label="First Name*"
                variant="outlined"
                margin="normal"
                onChange={(e) => setFirstName(e.target.value)}
                error={validateFields.firstName.invalid}
                helperText={validateFields.firstName.error}
                FormHelperTextProps={{ sx: { margin: '0px', backgroundColor: "#fcebed !important" } }}
                InputLabelProps={{
                  sx: {
                    color: firstName ? ACTIVE_COLOR : INACTIVE_COLOR,
                    "&.Mui-focused": {
                      color: ACTIVE_COLOR,
                    },
                    "&.MuiInputLabel-shrink": {
                      color: ACTIVE_COLOR,
                    },
                    "&:hover": {
                      color: ACTIVE_COLOR,
                    },
                  },
                }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                    "&:hover fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth
                label="Last Name*"
                variant="outlined"
                margin="normal"
                onChange={(e) => setLastName(e.target.value)}
                error={validateFields.lastName.invalid}
                helperText={validateFields.lastName.error}
                FormHelperTextProps={{ sx: { margin: '0px', backgroundColor: "#fcebed !important" } }}
                InputLabelProps={{
                  sx: {
                    color: lastName ? ACTIVE_COLOR : INACTIVE_COLOR,
                    "&.Mui-focused": {
                      color: ACTIVE_COLOR,
                    },
                    "&.MuiInputLabel-shrink": {
                      color: ACTIVE_COLOR,
                    },
                    "&:hover": {
                      color: ACTIVE_COLOR,
                    },
                  },
                }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                    "&:hover fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                margin="normal"
                onChange={(e) => setPhone(e.target.value)}
                error={validateFields.phone.invalid}
                helperText={validateFields.phone.error}
                FormHelperTextProps={{ sx: { margin: '0px', backgroundColor: "#fcebed !important" } }}
                InputLabelProps={{
                  sx: {
                    color: phone ? ACTIVE_COLOR : INACTIVE_COLOR,
                    "&.Mui-focused": {
                      color: ACTIVE_COLOR,
                    },
                    "&.MuiInputLabel-shrink": {
                      color: ACTIVE_COLOR,
                    },
                    "&:hover": {
                      color: ACTIVE_COLOR,
                    },
                  },
                }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                    "&:hover fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                  },
                }}
              />
            </Grid>  
          </Grid>

          <Grid size={6}>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {
              success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  We've sent a verification email to your address. Please check your inbox and follow the instructions to complete your sign-up.
                </Alert>
              )
            }
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                sx={{ mt: 2, backgroundColor: '#c3183c' }}
                onClick={handleSubmit}
                disabled={disabled}
              >
                UPDATE
              </Button>
              <Button
                variant="contained"
                sx={{ mt: 2, backgroundColor: '#c3183c' }}
                onClick={() => navigate("/")}
                disabled={disabled}
              >
                CANCEL
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default SignUpValidate;
